/**
 * Outing Create Flow - Popup-based step creation
 * Each step is a modal that leads to the next.
 * After completion, user lands on detail page with edit capability.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronRight, Check, Calendar, Users, Settings, 
  DollarSign, MapPin, X, Trophy, Target, Sparkles,
  Edit2, Flag
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Slider } from '../../../components/ui/slider';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import { CourseLookupModal } from '../../../components/score-entry';
import { useOutingApi } from '../hooks/useOutingApi';
import { GAME_FORMATS as GAME_FORMATS_OBJ } from '../utils/outingHelpers';

// Convert GAME_FORMATS object to array for iteration
const GAME_FORMATS = Object.entries(GAME_FORMATS_OBJ).map(([id, data]) => ({
  id,
  ...data
}));

// Default pars if no course selected
const DEFAULT_PARS = [4, 4, 3, 4, 5, 4, 3, 4, 5, 4, 4, 3, 4, 5, 4, 3, 4, 5];

// Popup Step Components
const StepPopup = ({ children, title, subtitle, onClose, step, totalSteps, footer }) => (
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[200] p-4 animate-fade-in">
    <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col animate-scale-in">
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold text-white">{title}</h2>
          {subtitle && <p className="text-sm text-zinc-400">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-zinc-500">Step {step}/{totalSteps}</span>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
      </div>
      
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        {children}
      </div>
      
      {/* Fixed Footer for Buttons */}
      {footer && (
        <div className="p-4 border-t border-white/10 flex-shrink-0 bg-[#1A1A1A]">
          {footer}
        </div>
      )}
    </div>
  </div>
);

// Step 1: Event Details
const EventDetailsStep = ({ data, onChange, onNext, onClose }) => {
  const [errors, setErrors] = useState({});
  
  const validate = () => {
    const newErrors = {};
    if (!data.name?.trim()) newErrors.name = 'Event name is required';
    if (!data.event_date) newErrors.event_date = 'Date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleNext = () => {
    if (validate()) onNext();
  };
  
  return (
    <StepPopup 
      title="Event Details" 
      subtitle="Name your golf outing"
      onClose={onClose}
      step={1}
      totalSteps={5}
      footer={
        <Button 
          onClick={handleNext}
          className="w-full h-12 bg-[#007AFF] hover:bg-[#0056b3] text-white font-semibold"
          data-testid="outing-details-next"
        >
          Next: Select Course
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      }
    >
      <div className="space-y-4">
        <div>
          <Label className="text-white mb-2 block">Event Name *</Label>
          <Input
            value={data.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="Annual Charity Classic"
            className="bg-[#0D0D0D] border-white/20 text-white h-12"
            data-testid="outing-name-input"
          />
          {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
        </div>
        
        <div>
          <Label className="text-white mb-2 block">Description</Label>
          <Textarea
            value={data.description}
            onChange={(e) => onChange('description', e.target.value)}
            placeholder="Tell players about this event..."
            className="bg-[#0D0D0D] border-white/20 text-white min-h-[80px]"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-white mb-2 block">Event Date *</Label>
            <Input
              type="date"
              value={data.event_date}
              onChange={(e) => onChange('event_date', e.target.value)}
              className="bg-[#0D0D0D] border-white/20 text-white h-12"
              data-testid="outing-date-input"
            />
            {errors.event_date && <p className="text-red-400 text-xs mt-1">{errors.event_date}</p>}
          </div>
          <div>
            <Label className="text-white mb-2 block">Tee Time</Label>
            <Input
              type="time"
              value={data.tee_time}
              onChange={(e) => onChange('tee_time', e.target.value)}
              className="bg-[#0D0D0D] border-white/20 text-white h-12"
            />
          </div>
        </div>
      </div>
    </StepPopup>
  );
};

// Step 2: Course Selection
const CourseStep = ({ data, onChange, onNext, onBack, onClose }) => {
  const [showCourseLookup, setShowCourseLookup] = useState(false);
  const [editingPars, setEditingPars] = useState(false);
  const [error, setError] = useState('');
  
  const handleCourseSelect = (course) => {
    onChange('course_name', course.name);
    onChange('course_id', course.course_id || course.id);
    setError(''); // Clear error when course selected
    
    // Set hole pars from course data
    if (course.hole_pars && course.hole_pars.length === 18) {
      onChange('hole_pars', course.hole_pars);
    } else if (course.holes && course.holes.length === 18) {
      const pars = course.holes.map(h => h.par || 4);
      onChange('hole_pars', pars);
    }
    setShowCourseLookup(false);
  };
  
  const handleNext = () => {
    if (!data.course_name?.trim()) {
      setError('Please select a course');
      return;
    }
    onNext();
  };
  
  const updatePar = (index, value) => {
    const newPars = [...data.hole_pars];
    newPars[index] = Math.max(3, Math.min(5, parseInt(value) || 4));
    onChange('hole_pars', newPars);
  };
  
  const front9Par = data.hole_pars.slice(0, 9).reduce((a, b) => a + b, 0);
  const back9Par = data.hole_pars.slice(9).reduce((a, b) => a + b, 0);
  const totalPar = front9Par + back9Par;
  
  return (
    <StepPopup 
      title="Course Selection" 
      subtitle="Choose where you're playing"
      onClose={onClose}
      step={2}
      totalSteps={5}
      footer={
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="flex-1 h-12 border-white/20 text-white"
          >
            Back
          </Button>
          <Button 
            onClick={handleNext}
            className="flex-1 h-12 bg-[#007AFF] hover:bg-[#0056b3] text-white font-semibold"
            data-testid="outing-course-next"
          >
            Next: Format
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Course Selection */}
        <div 
          onClick={() => setShowCourseLookup(true)}
          className={`p-4 rounded-xl border-2 border-dashed cursor-pointer transition-all text-center ${
            error ? 'border-red-500/50 bg-red-500/5' : 'border-white/20 hover:border-[#007AFF]/50'
          }`}
          data-testid="course-select-trigger"
        >
          {data.course_name ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MapPin className="w-6 h-6 text-green-400" />
                <span className="text-white font-semibold">{data.course_name}</span>
              </div>
              <Badge variant="outline" className="text-zinc-400">Change</Badge>
            </div>
          ) : (
            <>
              <MapPin className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
              <p className="text-zinc-400">Tap to search courses</p>
            </>
          )}
        </div>
        {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
        
        {/* Par Summary */}
        <Card className="bg-[#0D0D0D] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-semibold">Hole Pars</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingPars(!editingPars)}
                className="text-[#007AFF] h-8"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                {editingPars ? 'Done' : 'Edit'}
              </Button>
            </div>
            
            <div className="grid grid-cols-3 gap-2 text-center text-sm mb-3">
              <div className="bg-white/5 rounded-lg p-2">
                <p className="text-zinc-400 text-xs">Front 9</p>
                <p className="text-white font-bold">{front9Par}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-2">
                <p className="text-zinc-400 text-xs">Back 9</p>
                <p className="text-white font-bold">{back9Par}</p>
              </div>
              <div className="bg-green-500/20 rounded-lg p-2">
                <p className="text-green-400 text-xs">Total</p>
                <p className="text-green-400 font-bold">{totalPar}</p>
              </div>
            </div>
            
            {editingPars && (
              <div className="grid grid-cols-9 gap-1 mt-3">
                {data.hole_pars.map((par, i) => (
                  <div key={i} className="text-center">
                    <span className="text-[10px] text-zinc-500">{i + 1}</span>
                    <Input
                      type="number"
                      value={par}
                      onChange={(e) => updatePar(i, e.target.value)}
                      className="bg-[#1A1A1A] border-white/10 text-white text-center h-8 p-0 text-sm"
                      min={3}
                      max={5}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Course Lookup Modal */}
      <CourseLookupModal
        isOpen={showCourseLookup}
        onClose={() => setShowCourseLookup(false)}
        onCourseSelect={handleCourseSelect}
        title="Select Course"
      />
    </StepPopup>
  );
};

// Step 3: Game Format
const GameFormatStep = ({ data, onChange, onNext, onBack, onClose }) => {
  const updateGameSettings = (key, value) => {
    const newSettings = { ...data.game_settings, [key]: value };
    
    // When setting front_9_format, also set the main format
    if (key === 'front_9_format') {
      newSettings.format = value;
      // If same_format_all, sync back_9 as well
      if (newSettings.same_format_all) {
        newSettings.back_9_format = value;
      }
    }
    
    // When toggling same_format_all ON, sync back_9 to front_9
    if (key === 'same_format_all' && value === true) {
      newSettings.back_9_format = newSettings.front_9_format;
    }
    
    onChange('game_settings', newSettings);
  };
  
  return (
    <StepPopup 
      title="Game Format" 
      subtitle="Choose how teams will compete"
      onClose={onClose}
      step={3}
      totalSteps={5}
      footer={
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="flex-1 h-12 border-white/20 text-white"
          >
            Back
          </Button>
          <Button 
            onClick={onNext}
            className="flex-1 h-12 bg-[#007AFF] hover:bg-[#0056b3] text-white font-semibold"
            data-testid="outing-format-next"
          >
            Next: Prizes
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Split Format Toggle */}
        <Card className="bg-[#0D0D0D] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-white font-semibold">Split Format</h4>
                <p className="text-xs text-zinc-400">Different format for Front 9 vs Back 9</p>
              </div>
              <Switch
                checked={!data.game_settings.same_format_all}
                onCheckedChange={(v) => updateGameSettings('same_format_all', !v)}
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Format Selection */}
        <div>
          <Label className="text-white mb-2 block">
            {data.game_settings.same_format_all ? 'Game Format' : 'Front 9 Format'}
          </Label>
          <div className="grid grid-cols-2 gap-2">
            {GAME_FORMATS.map(fmt => (
              <button
                key={fmt.id}
                onClick={() => updateGameSettings('front_9_format', fmt.id)}
                className={`p-3 rounded-xl border-2 transition-all text-left ${
                  data.game_settings.front_9_format === fmt.id
                    ? 'border-[#007AFF] bg-[#007AFF]/10'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <span className="text-2xl mb-1 block">{fmt.icon}</span>
                <span className="text-white font-semibold text-sm">{fmt.name}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Back 9 Format (if split) */}
        {!data.game_settings.same_format_all && (
          <div>
            <Label className="text-white mb-2 block text-purple-400">Back 9 Format</Label>
            <div className="grid grid-cols-2 gap-2">
              {GAME_FORMATS.map(fmt => (
                <button
                  key={fmt.id}
                  onClick={() => updateGameSettings('back_9_format', fmt.id)}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    data.game_settings.back_9_format === fmt.id
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <span className="text-2xl mb-1 block">{fmt.icon}</span>
                  <span className="text-white font-semibold text-sm">{fmt.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Team Size */}
        <div>
          <Label className="text-white mb-2 block">Team Size</Label>
          <div className="flex gap-3">
            {[2, 4].map(size => (
              <Button
                key={size}
                variant={data.game_settings.team_size === size ? 'default' : 'outline'}
                className={`flex-1 h-12 ${data.game_settings.team_size === size ? 'bg-[#007AFF]' : 'border-white/20 text-white'}`}
                onClick={() => updateGameSettings('team_size', size)}
              >
                {size}-Man Teams
              </Button>
            ))}
          </div>
        </div>
      </div>
    </StepPopup>
  );
};

// Step 4: Skins & CTP
const PrizesStep = ({ data, onChange, onNext, onBack, onClose }) => {
  const updateSkins = (key, value) => {
    onChange('game_settings', {
      ...data.game_settings,
      skins: { ...data.game_settings.skins, [key]: value }
    });
  };
  
  const updateCTP = (key, value) => {
    onChange('game_settings', {
      ...data.game_settings,
      ctp: { ...data.game_settings.ctp, [key]: value }
    });
  };
  
  // Auto-select par 3 holes for CTP
  useEffect(() => {
    if (data.game_settings.ctp.enabled && data.game_settings.ctp.holes.length === 0) {
      const par3Holes = data.hole_pars
        .map((par, i) => par === 3 ? i + 1 : null)
        .filter(h => h !== null);
      updateCTP('holes', par3Holes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.game_settings.ctp.enabled]);
  
  const toggleCTPHole = (hole) => {
    const holes = data.game_settings.ctp.holes;
    const newHoles = holes.includes(hole)
      ? holes.filter(h => h !== hole)
      : [...holes, hole].sort((a, b) => a - b);
    updateCTP('holes', newHoles);
  };
  
  return (
    <StepPopup 
      title="Side Games & Prizes" 
      subtitle="Add skins and closest to pin"
      onClose={onClose}
      step={4}
      totalSteps={5}
      footer={
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="flex-1 h-12 border-white/20 text-white"
          >
            Back
          </Button>
          <Button 
            onClick={onNext}
            className="flex-1 h-12 bg-[#007AFF] hover:bg-[#0056b3] text-white font-semibold"
            data-testid="outing-prizes-next"
          >
            Next: Entry Fee
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Skins Game */}
        <Card className="bg-[#0D0D0D] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">Skins Game</h4>
                  <p className="text-xs text-zinc-400">Lowest score wins each hole</p>
                </div>
              </div>
              <Switch
                checked={data.game_settings.skins.enabled}
                onCheckedChange={(v) => updateSkins('enabled', v)}
              />
            </div>
            
            {data.game_settings.skins.enabled && (
              <div className="space-y-3 pt-3 border-t border-white/10">
                <div>
                  <Label className="text-white text-sm">Total Skins Pot: ${data.game_settings.skins.total_pot}</Label>
                  <Slider
                    value={[data.game_settings.skins.total_pot]}
                    onValueChange={(v) => updateSkins('total_pot', v[0])}
                    min={18}
                    max={900}
                    step={18}
                    className="mt-2"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    = ${(data.game_settings.skins.total_pot / 18).toFixed(2)} per hole
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">Carryover on ties</span>
                  <Switch
                    checked={data.game_settings.skins.carryover}
                    onCheckedChange={(v) => updateSkins('carryover', v)}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Closest to Pin */}
        <Card className="bg-[#0D0D0D] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                  <Target className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">Closest to Pin</h4>
                  <p className="text-xs text-zinc-400">Par 3 hole contests</p>
                </div>
              </div>
              <Switch
                checked={data.game_settings.ctp.enabled}
                onCheckedChange={(v) => updateCTP('enabled', v)}
              />
            </div>
            
            {data.game_settings.ctp.enabled && (
              <div className="space-y-3 pt-3 border-white/10">
                <div>
                  <Label className="text-white text-sm mb-2 block">CTP Holes (Par 3s)</Label>
                  <div className="flex flex-wrap gap-2">
                    {data.hole_pars.map((par, i) => {
                      const hole = i + 1;
                      if (par !== 3) return null;
                      const isSelected = data.game_settings.ctp.holes.includes(hole);
                      return (
                        <button
                          key={hole}
                          onClick={() => toggleCTPHole(hole)}
                          className={`px-3 py-1 rounded-full text-sm font-semibold transition-all ${
                            isSelected 
                              ? 'bg-yellow-500 text-black' 
                              : 'bg-white/10 text-zinc-400 hover:bg-white/20'
                          }`}
                        >
                          Hole {hole}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <Label className="text-white text-sm">Total CTP Pot: ${data.game_settings.ctp.total_pot}</Label>
                  <Slider
                    value={[data.game_settings.ctp.total_pot]}
                    onValueChange={(v) => updateCTP('total_pot', v[0])}
                    min={0}
                    max={500}
                    step={25}
                    className="mt-2"
                  />
                  {data.game_settings.ctp.holes.length > 0 && (
                    <p className="text-xs text-zinc-500 mt-1">
                      = ${(data.game_settings.ctp.total_pot / data.game_settings.ctp.holes.length).toFixed(2)} per hole
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </StepPopup>
  );
};

// Step 5: Entry Fee + Create
const EntryFeeStep = ({ data, onChange, onBack, onClose, onSubmit, saving }) => {
  const format = GAME_FORMATS.find(f => f.id === data.game_settings.front_9_format);
  const back9Format = GAME_FORMATS.find(f => f.id === data.game_settings.back_9_format);
  
  return (
    <StepPopup 
      title="Entry Fee" 
      subtitle="Set player contribution"
      onClose={onClose}
      step={5}
      totalSteps={5}
      footer={
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="flex-1 h-12 border-white/20 text-white"
            disabled={saving}
          >
            Back
          </Button>
          <Button 
            onClick={onSubmit}
            disabled={saving}
            className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white font-semibold"
            data-testid="outing-create-btn"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating...
              </span>
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                Create Outing
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Entry Fee */}
        <div className="text-center py-6">
          <span className="text-6xl font-black text-[#007AFF]">
            ${data.entry_fee}
          </span>
          <p className="text-zinc-400 text-sm mt-2">per player</p>
        </div>
        
        <Slider
          value={[data.entry_fee]}
          onValueChange={(v) => onChange('entry_fee', v[0])}
          min={0}
          max={500}
          step={5}
        />
        <div className="flex justify-between text-xs text-zinc-500">
          <span>Free</span>
          <span>$500</span>
        </div>
        
        {/* Quick Summary */}
        <Card className="bg-[#0D0D0D] border-white/10">
          <CardContent className="p-4">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#007AFF]" />
              Event Summary
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-400">Event</span>
                <span className="text-white font-medium">{data.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Course</span>
                <span className="text-white">{data.course_name || 'Not selected'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Format</span>
                <span className="text-white">
                  {format?.name}
                  {!data.game_settings.same_format_all && ` / ${back9Format?.name}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Teams</span>
                <span className="text-white">{data.game_settings.team_size}-Man</span>
              </div>
              {data.game_settings.skins.enabled && (
                <div className="flex justify-between">
                  <span className="text-zinc-400">Skins Pot</span>
                  <span className="text-amber-400">${data.game_settings.skins.total_pot}</span>
                </div>
              )}
              {data.game_settings.ctp.enabled && (
                <div className="flex justify-between">
                  <span className="text-zinc-400">CTP Pot</span>
                  <span className="text-yellow-400">${data.game_settings.ctp.total_pot}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </StepPopup>
  );
};

// Main Flow Component
export default function OutingCreateFlow({ isOpen, onClose, user }) {
  const navigate = useNavigate();
  const api = useOutingApi();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    event_date: '',
    tee_time: '08:00',
    course_name: '',
    course_id: null,
    hole_pars: [...DEFAULT_PARS],
    game_settings: {
      front_9_format: 'scramble',
      back_9_format: 'scramble',
      same_format_all: true,
      team_size: 4,
      skins: { enabled: false, total_pot: 90, carryover: true },
      ctp: { enabled: true, holes: [], total_pot: 100 }
    },
    entry_fee: 100
  });
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async () => {
    // Validate user is logged in
    if (!user?.user_id) {
      toast.error('You must be logged in to create an outing');
      return;
    }
    
    setSaving(true);
    try {
      const result = await api.createOuting(formData, user.user_id, user.name || 'Organizer');
      if (result.success) {
        toast.success('Outing created successfully!');
        onClose();
        navigate(`/outings/${result.outing.id}`);
      } else {
        toast.error(result.error || 'Failed to create outing');
      }
    } catch (err) {
      toast.error('Failed to create outing');
    } finally {
      setSaving(false);
    }
  };
  
  const handleClose = () => {
    if (formData.name || formData.course_name) {
      if (window.confirm('Discard changes?')) {
        onClose();
        setStep(1);
        setFormData({
          name: '',
          description: '',
          event_date: '',
          tee_time: '08:00',
          course_name: '',
          course_id: null,
          hole_pars: [...DEFAULT_PARS],
          game_settings: {
            front_9_format: 'scramble',
            back_9_format: 'scramble',
            same_format_all: true,
            team_size: 4,
            skins: { enabled: false, total_pot: 90, carryover: true },
            ctp: { enabled: true, holes: [], total_pot: 100 }
          },
          entry_fee: 100
        });
      }
    } else {
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      {step === 1 && (
        <EventDetailsStep 
          data={formData} 
          onChange={updateField}
          onNext={() => setStep(2)}
          onClose={handleClose}
        />
      )}
      {step === 2 && (
        <CourseStep 
          data={formData}
          onChange={updateField}
          onNext={() => setStep(3)}
          onBack={() => setStep(1)}
          onClose={handleClose}
        />
      )}
      {step === 3 && (
        <GameFormatStep 
          data={formData}
          onChange={updateField}
          onNext={() => setStep(4)}
          onBack={() => setStep(2)}
          onClose={handleClose}
        />
      )}
      {step === 4 && (
        <PrizesStep 
          data={formData}
          onChange={updateField}
          onNext={() => setStep(5)}
          onBack={() => setStep(3)}
          onClose={handleClose}
        />
      )}
      {step === 5 && (
        <EntryFeeStep 
          data={formData}
          onChange={updateField}
          onBack={() => setStep(4)}
          onClose={handleClose}
          onSubmit={handleSubmit}
          saving={saving}
        />
      )}
    </>
  );
}
